/**
 * O rodapé: especificações técnicas, materiais e a tabela de entregas.
 *
 * As especificações são pares `CHAVE: valor` em duas colunas. Uma chave que o
 * leitor não conhece **não derruba a importação** — vai para `outros` e vira
 * aviso. Recusar uma OS porque o sistema da gráfica ganhou um campo novo seria
 * parar a produção por causa de uma etiqueta.
 */

import type { EspecificacoesLidas, MaterialLido } from '@pcpgbu/dominio';
import {
  agruparEmLinhas,
  celula,
  colunasDaLinha,
  numeroBr,
  registrosDaTabela,
  type Linha,
} from './grade.js';
import type { ItemDeTexto } from './pdf.js';

const RE_INICIO_ESPEC = /^Especificações técnicas/;
const RE_INICIO_MATERIAIS = /^Materiais para OS:/;
const RE_INICIO_ENTREGAS = /^Cliente\/Produto/;

/** As chaves conhecidas, na ordem em que aparecem impressas. */
const CHAVES: Array<{ campo: keyof Omit<EspecificacoesLidas, 'outros'>; re: RegExp }> = [
  { campo: 'entrega', re: /^ENTREGA:\s*(.*)$/ },
  { campo: 'nota', re: /^NOTA:\s*(.*)$/ },
  { campo: 'ultimaOs', re: /^Mês e Ano da Última "OS":\s*(.*)$/ },
  { campo: 'arquivar', re: /^ARQUIVAR "OS"\?:\s*(.*)$/ },
  { campo: 'ordemCompraCliente', re: /^ORDEM DE COMPRA DO CLIENTE:\s*(.*)$/ },
  { campo: 'acabamentoAdesivoVinil', re: /^ACABAMENTO ADESIVO VINIL:\s*(.*)$/ },
];

export function lerEspecificacoes(
  itens: readonly ItemDeTexto[],
  avisos: string[],
): EspecificacoesLidas {
  const espec: EspecificacoesLidas = {
    entrega: null,
    nota: null,
    ultimaOs: null,
    arquivar: null,
    ordemCompraCliente: null,
    acabamentoAdesivoVinil: null,
    outros: {},
  };

  const inicio = itens.filter((i) => RE_INICIO_ESPEC.test(i.texto)).sort((a, b) => a.y - b.y)[0];
  if (!inicio) return espec;
  const fim = acharY(itens, RE_INICIO_MATERIAIS, inicio.y) ?? Number.POSITIVE_INFINITY;

  const dentro = itens.filter((i) => i.y > inicio.y + 2 && i.y < fim - 2);

  for (const item of dentro) {
    const texto = item.texto.trim();
    let casou = false;
    for (const { campo, re } of CHAVES) {
      const m = re.exec(texto);
      if (m) {
        espec[campo] = (m[1] ?? '').trim() || null;
        casou = true;
        break;
      }
    }
    if (casou) continue;

    const generico = /^([^:]{2,60}):\s*(.*)$/.exec(texto);
    if (generico) {
      const chave = (generico[1] ?? '').trim();
      espec.outros[chave] = (generico[2] ?? '').trim();
      avisos.push(`Especificação técnica desconhecida: "${chave}".`);
    } else {
      avisos.push(`Linha solta nas especificações técnicas: "${texto}"`);
    }
  }

  return espec;
}

/** "01278-Chapa ECO3 SM 74 (605x745x30) - ELIPSE" → código + nome. */
const RE_CODIGO = /^(\d{4,6})-(.*)$/;
/** "1.095 fl", "0,29 kg", "4 un" — a unidade vem colada na quantidade. */
const RE_QTD = /^([\d.,]+)\s*(un|fl|kg|mt|lt)$/;
/** "9,50 %" */
const RE_QUEBRA = /^([\d.,]+)\s*%$/;

export function lerMateriais(itens: readonly ItemDeTexto[], avisos: string[]): MaterialLido[] {
  const inicioItem = itens
    .filter((i) => RE_INICIO_MATERIAIS.test(i.texto))
    .sort((a, b) => a.y - b.y)[0];
  if (!inicioItem) return [];

  const fim = acharY(itens, RE_INICIO_ENTREGAS, inicioItem.y) ?? Number.POSITIVE_INFINITY;

  const linhas = agruparEmLinhas(itens.filter((i) => i.y >= inicioItem.y - 2 && i.y < fim - 2));
  const cabecalho = linhas[0];
  if (!cabecalho) return [];

  const colunas = colunasDaLinha(cabecalho);
  const iPlano = colunas.findIndex((c) => /^Plano$/.test(c.nome));
  const iQtd = colunas.findIndex((c) => /^Qtdade$/.test(c.nome));
  const iQuebra = colunas.findIndex((c) => /^Quebra$/.test(c.nome));
  const iObs = colunas.findIndex((c) => /^Observações$/.test(c.nome));

  if (iQtd < 0) {
    avisos.push('Tabela de materiais sem a coluna "Qtdade".');
    return [];
  }

  const registros = registrosDaTabela(linhas.slice(1), colunas);
  const materiais: MaterialLido[] = [];

  for (const r of registros) {
    const nomeBruto = celula(r, 0);
    if (!nomeBruto) continue;

    const qtdTexto = celula(r, iQtd);
    if (!qtdTexto) {
      avisos.push(`Material sem quantidade: "${nomeBruto}"`);
      continue;
    }
    const mq = RE_QTD.exec(qtdTexto);
    if (!mq) {
      avisos.push(`Quantidade de material não reconhecida: "${qtdTexto}" (${nomeBruto})`);
      continue;
    }

    const mc = RE_CODIGO.exec(nomeBruto);
    const planoTexto = iPlano >= 0 ? celula(r, iPlano) : null;
    const quebraTexto = iQuebra >= 0 ? celula(r, iQuebra) : null;
    const mQuebra = quebraTexto ? RE_QUEBRA.exec(quebraTexto) : null;

    materiais.push({
      codigo: mc ? (mc[1] ?? null) : null,
      nome: mc ? (mc[2] ?? '').trim() : nomeBruto,
      plano: planoTexto && /^\d+$/.test(planoTexto) ? Number(planoTexto) : null,
      quantidade: numeroBr(mq[1] ?? null) ?? 0,
      unidade: mq[2] ?? '',
      quebraPct: mQuebra ? numeroBr(mQuebra[1] ?? null) : null,
      observacoes: iObs >= 0 ? celula(r, iObs) : null,
    });
  }

  return materiais;
}

/**
 * A tabela de entregas aparece em uma OS a cada vinte. Enquanto o uso dela não
 * for confirmado com a gráfica (PRD §17.1 J-11), guardamos as linhas cruas: é
 * pouco espaço e evita inventar um significado que depois não se sustenta.
 */
export function lerEntregas(itens: readonly ItemDeTexto[]): string[] {
  const inicio = itens.filter((i) => RE_INICIO_ENTREGAS.test(i.texto)).sort((a, b) => a.y - b.y)[0];
  if (!inicio) return [];
  const linhas = agruparEmLinhas(itens.filter((i) => i.y > inicio.y + 2));
  return linhas
    .map((l: Linha) => l.itens.map((i) => i.texto).join(' ').trim())
    .filter((t) => t.length > 0);
}

function acharY(itens: readonly ItemDeTexto[], re: RegExp, depoisDe: number): number | null {
  const achado = itens
    .filter((i) => re.test(i.texto) && i.y > depoisDe)
    .sort((a, b) => a.y - b.y)[0];
  return achado ? achado.y : null;
}
